//src/routes/_layout/coversations.tsx
import {
  Box,
  Center,
  VStack,
  Text,
  SimpleGrid,
  Button,
  Flex,
} from "@chakra-ui/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { ChatService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";
import { usePendingMessage } from "@/context/PendingMessageContext";
import { ChatInput } from "@/components/ui/chat-input";

export const Route = createFileRoute("/_layout/conversations")({
  component: Chats,
});

function Chats() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showErrorToast } = useCustomToast();
  const { pendingMessage, clearPendingMessage, setPendingMessage } =
    usePendingMessage();
  const [isLoading, setIsLoading] = useState(false);

  // Example questions with icons/emojis
  const exampleQuestions = [
    { text: "이번주 일정 알려줘", emoji: "🗓️" },
    { text: "염색 레시피 추천해줘", emoji: "🧪" },
    { text: "베어링 결함 종류에 대해서 알려줘", emoji: "⚙️" },
    { text: "회의록 내용 정리해줘", emoji: "📝" },
  ];

  // Create a conversation
  const createChatMutation = useMutation({
    mutationFn: async ({
      msg,
      options: _options,
    }: {
      msg: string;
      options?: Record<string, unknown>;
    }) => {
      setIsLoading(true);

      // 대화 생성
      const chatResponse = await ChatService.createConversation({
        message: msg,
      });

      return {
        chatResponse,
      };
    },
    onSuccess: async (response, variables) => {
      const { msg, options } = variables;
      const { chatResponse } = response;

      if (!chatResponse?.id) {
        showErrorToast("❌ Failed to create conversation");
        return;
      }

      // 일반 채팅 옵션 설정
      const chatOptions = {
        ...options,
      };

      setPendingMessage({ msg, options: chatOptions });
      navigate({ to: `/chat/${chatResponse.id}` });

      await queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (error) => {
      console.error("❌ Request failed:", error);
      showErrorToast("❌ 요청에 실패했습니다. 다시 시도해주세요.");
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
  const handleStartChat = (msg: string, options?: Record<string, unknown>) => {
    if (!msg.trim()) return;

    console.log("Starting chat with:", { msg, options });

    // 기본 옵션 설정 (ChatInput에서 설정하는 것과 동일하게)
    const defaultOptions = {
      tables: [],
      folders: [],
      tools: [],
      ...options,
    };

    createChatMutation.mutate({ msg, options: defaultOptions });
  };

  const handleExampleClick = (question: string) => {
    console.log("Example question clicked:", question);
    handleStartChat(question);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pendingMessage) {
      if (pendingMessage.msg.trim()) {
        createChatMutation.mutate({
          msg: pendingMessage.msg,
          options: pendingMessage.options,
        });
      }
      clearPendingMessage();
    }
  }, [pendingMessage, createChatMutation.mutate, clearPendingMessage]);

  return (
    <Center w="100vw" h="100vh">
      <Box w="100%" maxW="600px" mx="auto" px={6} py={8}>
        <VStack gap={8} w="100%">
          {/* Header */}
          <VStack gap={3} textAlign="center">
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="700"
              color="gray.800"
              letterSpacing="-0.025em"
            >
              새로운 대화 시작
            </Text>
            <Text fontSize="lg" color="gray.600" fontWeight="400">
              무엇을 도와드릴까요?
            </Text>
          </VStack>
          {/* Chat Input Card */}
          <Box
            w="100%"
            bg="white"
            borderRadius="3xl"
            // boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
            p={6}
            // border="1px solid"
            borderColor="gray.100"
          >
            <ChatInput isStreaming={isLoading} onSend={handleStartChat} />
          </Box>

          {/* Example Questions */}
          <VStack gap={5} w="100%">
            <Text
              fontSize="sm"
              color="gray.500"
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="0.1em"
            >
              예시 질문
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="100%">
              {exampleQuestions.map((item) => (
                <Button
                  key={item.text}
                  variant="outline"
                  size="lg"
                  onClick={() => handleExampleClick(item.text)}
                  disabled={isLoading}
                  bg="gray.50"
                  borderColor="gray.200"
                  _hover={{
                    bg: "gray.100",
                    borderColor: "gray.300",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                  _active={{
                    transform: "translateY(0)",
                  }}
                  whiteSpace="normal"
                  height="auto"
                  py={5}
                  px={5}
                  borderRadius="xl"
                  justifyContent="flex-start"
                  textAlign="left"
                  transition="all 0.3s ease"
                >
                  <Flex align="center" gap={4} w="100%">
                    <Box
                      fontSize="2xl"
                      minW="40px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {item.emoji}
                    </Box>
                    <Text
                      fontSize="sm"
                      fontWeight="500"
                      color="gray.700"
                      lineHeight="1.5"
                      textAlign="left"
                    >
                      {item.text}
                    </Text>
                  </Flex>
                </Button>
              ))}
            </SimpleGrid>
          </VStack>
        </VStack>
      </Box>
    </Center>
  );
}

export default Chats;
